import { emit } from '../utils/GlobalEventDispatcher';
import type { PlayerPrize, Campaign } from '../types/types';
import type { DropAPI } from '@/types/DropAPI';

const HIDDEN_RETRY_INTERVAL = 180000;
const CAMPAIGN_RETRY_INTERVAL = 180000;
const COIN_UPDATE_INTERVAL = 60000;
const PLAYER_WIN_TIMEOUT = 60000;

export interface ServerOptions {
	token: string;
	url: string;
	identifier: string;
}

// Define event names as constants
export const SERVER_EVENTS = {
	ACTIVE: 'server:active',
	COIN_UPDATE: 'server:coinUpdate',
	CAMPAIGN_UPDATE: 'server:campaignUpdate',
	PLAYER_WIN: 'server:playerWin',
	ERROR: 'server:error',
	CHANGE_MODE: 'server:changeMode'
} as const;

/**
 *
 * setUp
 * activate
 * stop
 * campaignId
 * currentWin
 * winConfirm

 *
 *
 * events: coin_info, win, error
 *
 * //"https://loyalty-drops.k3s-dev.bgaming-system.com/api/v1/"
 *
 * @returns
 */
/*
ServerMode -
hidden mode - server is hidden, no any campaign
passive mode -  server pings for current campaings
active mode - server has current campaing and ping coin info for it
finishing mode - server has current campaing which is ended (time, prizes)
but player prizes still comming

*/
export enum ServerMode {
	HIDDEN = 'hidden_mode',
	PASSEIVE = 'passive_mode',
	ACTIVE = 'active_mode',
	FINISHEING = 'finishing_mode'
}

export function useServer(options: ServerOptions, dropAPI: DropAPI) {
	const { token, url, identifier } = options;
	const { fetchDropList, fetchCoin, fetchPlayerWin } = dropAPI;

	let currentCampaign: Campaign | null | undefined;
	let serverMode: ServerMode = ServerMode.PASSEIVE;

	// console.log('USE SERVER| options:', options);

	const start = async () => {
		//clearAllIntervals();
		// console.info('Drop server start', url, token, identifier);
		let camp: Campaign | null | undefined | 'empty';
		try {
			camp = await findCurrentCampaign();
		} catch (error) {
			// console.log('start error', error);
			camp = null;
		}
		if (camp === 'empty') {
			setHiddenMode();
			//setPassiveMode();
			return;
		}

		// console.log('camp', camp);

		if (!camp) {
			if (serverMode === ServerMode.FINISHEING) {
				setFinishingMode();
			} else {
				setPassiveMode();
			}
			return;
		}

		if (serverMode === ServerMode.FINISHEING && !camp.player_joined) {
			setFinishingMode();
			return;
		}

		//one mode for joined and not joined campaign
		//in getPlayerWin we check if campaign is joined or not
		setActiveMode(camp);
	};

	let campaignRetryInterval: any;
	const setActiveMode = (camp: Campaign) => {
		clearInterval(campaignRetryInterval);
		serverMode = ServerMode.ACTIVE;
		currentCampaign = camp;
		emit(SERVER_EVENTS.CHANGE_MODE, { mode: serverMode, campaign: currentCampaign });
		pingCoinInfo();
		getPlayerWin();
	};

	const setHiddenMode = () => {
		serverMode = ServerMode.HIDDEN;
		currentCampaign = null;
		clearAllIntervals();
		emit(SERVER_EVENTS.CHANGE_MODE, { mode: serverMode, campaign: currentCampaign });
		campaignRetryInterval = setTimeout(() => {
			start();
		}, HIDDEN_RETRY_INTERVAL);
	};

	const setPassiveMode = () => {
		serverMode = ServerMode.PASSEIVE;
		currentCampaign = null;
		clearAllIntervals();
		emit(SERVER_EVENTS.CHANGE_MODE, { mode: serverMode, campaign: currentCampaign });
		//clearInterval(coinInterval)
		campaignRetryInterval = setTimeout(() => {
			start();
		}, CAMPAIGN_RETRY_INTERVAL);
	};

	const setFinishingMode = () => {
		serverMode = ServerMode.FINISHEING;
		clearInterval(coinInterval);
		//pingPlayerWin();
		getPlayerWin();
		emit(SERVER_EVENTS.CHANGE_MODE, { mode: serverMode, campaign: currentCampaign });
	};

	const findCurrentCampaign = async (): Promise<Campaign | null | undefined | 'empty'> => {
		const result = await fetchDropList();

		if (!result) {
			return 'empty';
		}

		if (result.length === 0) {
			return 'empty';
		}

		// console.log('find drop list ', result);
		let findedCamp: Campaign | null | undefined = null;
		if (result) {
			const drops = result;
			findedCamp = drops.find(
				(drop: Campaign) => drop.status === 'ongoing' && drop.game_identifiers.includes(identifier)
			);
		}
		return findedCamp;
	};

	let coinInterval: any;
	const pingCoinInfo = async () => {
		internalInvokeCoinInfo();
		coinInterval = setInterval(async () => {
			internalInvokeCoinInfo();
		}, COIN_UPDATE_INTERVAL);
	};

	const internalInvokeCoinInfo = async () => {
		try {
			const result = await fetchCoin(getId());
			// console.log('ping coin info result', result);

			if (result && result.drop_campaign_status == 'ongoing') {
				emit(SERVER_EVENTS.COIN_UPDATE, result);
			} else {
				emit(SERVER_EVENTS.COIN_UPDATE, {
					drop_campaign_status: 'archived',
					total_remaining_prizes: 0,
					max_prize: 0
				});
				if (currentCampaign && currentCampaign.player_joined) {
					setFinishingMode();
				} else {
					setPassiveMode();
				}
			}
		} catch (error) {
			console.error('get coin info error', error);
			setPassiveMode();
		}
	};

	let playerWinInterval: any;
	const pingPlayerWin = async () => {
		// console.log('pingPlayerWin.............');
		clearTimeout(playerWinInterval);
		try {
			playerWinInterval = setTimeout(async () => {
				await getPlayerWin();
			}, PLAYER_WIN_TIMEOUT);
		} catch (error) {
			// console.log('clear player ping');
			clearInterval(playerWinInterval);
			emit(SERVER_EVENTS.ERROR, 'player win error');
		}
	};

	const getPlayerWin = async () => {
		if (!currentCampaign || !currentCampaign.player_joined) {
			return;
		}
		try {
			clearTimeout(playerWinInterval);
			const result = await fetchPlayerWin(getId());
			// console.log('getPlayerWIN !!! result', result);
			if (result) {
				let prize = result as PlayerPrize;

				emit(SERVER_EVENTS.PLAYER_WIN, result as PlayerPrize);
			}
			if (result == null || result?.amount_cents_in_campaign_currency == null) {
				if (serverMode == ServerMode.FINISHEING) {
					//Campaign is finished, not more prizes
					setPassiveMode();
				} else {
					// console.log('clear player ping');
					//this only one place where code invoke playe wins ping
					pingPlayerWin();
				}
			}
		} catch (error) {
			console.error('get player win error', error);
			//clearInterval(playerWinInterval);
			pingPlayerWin();
			emit(SERVER_EVENTS.ERROR, 'player win error');
		}
	};

	const confirmPlayerWin = async (prize: PlayerPrize) => {
		//TODO fetch post
		try {
			const result = await dropAPI.confirmPlayerWin(prize.id.toString());
			// console.log('confirmPlayerWin !!! result', result);
			//pingPlayerWin();
			getPlayerWin();
		} catch (error) {
			getPlayerWin();
			console.error('confirm player win error', error);
			emit(SERVER_EVENTS.ERROR, 'confirm player win error');
		}
	};

	const clearAllIntervals = () => {
		clearInterval(coinInterval);
		clearInterval(campaignRetryInterval);
		clearInterval(playerWinInterval);
	};

	function getId(): string | null {
		if (currentCampaign) {
			return currentCampaign.id.toString();
		}
		return '';
	}

	return {
		start,
		getPlayerWin,
		confirmPlayerWin,
		stop: clearAllIntervals
	};
}
